/**
 * @module timeoutSignal
 * @description `AbortSignal.timeout()` ponyfill for old WebViews.
 *
 * `AbortSignal.timeout` only exists from Chrome/WebView 103 (2022-06). Android TV
 * boxes and smart-TV browsers commonly ship older WebViews, where the call throws
 * `TypeError: AbortSignal.timeout is not a function`. Because every call site sits
 * inside a `try` / `.catch()`, that TypeError gets swallowed and surfaces as a
 * generic "Unable to connect to server" — even though the network is fine.
 *
 * Vite only transpiles syntax, never runtime APIs, so a build target change does
 * not help here. Always use this helper instead of `AbortSignal.timeout` directly.
 */
export function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }

  const controller = new AbortController();
  setTimeout(() => {
    try {
      // `abort(reason)` needs Chrome 98+; older engines ignore the argument.
      controller.abort(new DOMException('The operation timed out.', 'TimeoutError'));
    } catch {
      controller.abort();
    }
  }, ms);
  return controller.signal;
}
